import { FilterList, Filters } from 'common/components/AssetBrowser/lib/helpers/enabled_filters';
import { AnyAction } from 'redux';
import { ThunkAction } from '@reduxjs/toolkit';
import { I18n } from 'common/i18n';
import * as filters from 'common/components/AssetBrowser/actions/filters';
import { FilterType } from './types';
import { FilterScope, getScope } from './translations';

import { assetTypeConfigs } from './asset-type';
import { authorityConfigs } from './authority';
import { sourceConfigs } from './source';
import { versionConfigs } from './version';
import { visibilityConfigs } from './visibility';
import { IBaseListDropdownOption } from '@tylertech/forge/esm/list-dropdown';
import { ownedByConfigs } from './owned-by';
import { tagConfigs } from './tag';
import { categoryConfigs } from './category';
import { requesterConfigs } from './requester';

interface BaseConfig {
  filterName: Filters;
  label: string;
}

export interface CheckboxConfig extends BaseConfig {
  dispatchAction: () => AppThunk;
  filterType: FilterType.Checkbox;
}

export interface SelectDropdownConfig extends BaseConfig {
  getOptions: (list?: string[]) => IBaseListDropdownOption<string | null>[];
  dispatchAction: (value: string | null) => AppThunk;
  filterType: FilterType.SelectDropdown;
  filterListName?: FilterList;
}

export interface AutocompleteDropdownConfig<T> extends BaseConfig {
  placeholder: string;
  filterCallback: (
    input: string,
    list?: T[],
    showTeamsOnly?: boolean,
  ) => IBaseListDropdownOption<string | null>[];
  /**
   * Object is only for the Owned By filter
   */
  dispatchAction: (value: string | object | null) => AppThunk;
  filterType: FilterType.AutocompleteDropdown;
  filterListName: FilterList;
  transformSelection?: (selectedOption: string | null, list?: T[]) => string | object | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Union type requires `any` to accommodate different AutocompleteDropdownConfig generic instantiations (User, string, etc.)
type FilterEntry = CheckboxConfig | SelectDropdownConfig | AutocompleteDropdownConfig<any>;

type AppThunk = ThunkAction<any, any, unknown, AnyAction>;

export const FilterLookup: Partial<Record<Filters, FilterEntry>> = {
  // Checkbox filters
  onlyRecentlyViewed: {
    filterName: Filters.OnlyRecentlyViewed,
    label: I18n.t('label', { scope: getScope(FilterScope.RecentlyViewed) }),
    dispatchAction: filters.toggleRecentlyViewed,
    filterType: FilterType.Checkbox,
  },
  enrolledInArchival: {
    filterName: Filters.EnrolledInArchival,
    label: I18n.t('label', { scope: getScope(FilterScope.EnrolledInArchival) }),
    dispatchAction: filters.toggleEnrolledInArchival,
    filterType: FilterType.Checkbox,
  },
  approvalStatus: {
    filterName: Filters.ApprovalStatus,
    label: I18n.t('awaiting_approval', { scope: 'shared.asset_browser.filters' }),
    dispatchAction: filters.toggleAwaitingApproval,
    filterType: FilterType.Checkbox,
  },
  showInternalTargetAudience: {
    filterName: Filters.ShowInternalTargetAudience,
    label: I18n.t('internal', { scope: getScope(FilterScope.ApprovalTypes) }),
    dispatchAction: filters.toggleInternalTargetAudience,
    filterType: FilterType.Checkbox,
  },
  showPublicTargetAudience: {
    filterName: Filters.ShowPublicTargetAudience,
    label: I18n.t('public', { scope: getScope(FilterScope.ApprovalTypes) }),
    dispatchAction: filters.togglePublicTargetAudience,
    filterType: FilterType.Checkbox,
  },
  // Select dropdown filters
  authority: authorityConfigs,
  assetTypes: assetTypeConfigs,
  source: sourceConfigs,
  version: versionConfigs,
  visibility: visibilityConfigs,
  // Autocomplete dropdown filters
  ownedBy: ownedByConfigs,
  category: categoryConfigs,
  tag: tagConfigs,
  // TODO EN-85982: Follow-up ticket for custom facet
  // customFacet: customFacetConfigs,
  requester: requesterConfigs,
};
